<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="700px"
    overlay-opacity="0.85"
    content-class="modern-shift-dialog"
  >
    <v-card class="rounded-xl" elevation="16">
      <!-- Header with gradient background -->
      <v-card-title
        class="editor-header d-flex justify-space-between align-center px-6 py-4"
      >
        <div class="text-h5 font-weight-bold white--text">
          <v-icon large color="white" class="mr-3">mdi-calendar-plus</v-icon>
          Create New Shift
        </div>
        <v-btn
          icon
          @click="onClose"
          class="close-btn"
          color="white"
          depressed
          large
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Main content -->
      <v-card-text class="px-6 py-4">
        <v-row>
          <!-- Shift Title -->
          <v-col cols="12" class="py-2">
            <v-text-field
              v-model="title"
              label="Shift Title*"
              outlined
              dense
              prepend-inner-icon="mdi-format-title"
              :rules="[rules.required]"
              @input="titleMessage = null"
              class="modern-input"
              placeholder="Morning shift or Shift 1"
            />
          </v-col>

          <!-- Shift Description -->
          <v-col cols="12" class="py-2">
            <v-textarea
              v-model="description"
              label="Shift Description"
              outlined
              dense
              prepend-inner-icon="mdi-text"
              rows="2"
              class="modern-input"
              placeholder="No prior experience needed. Please bring gardening gloves and wear comfortable clothing."
            />
          </v-col>

          <!-- Date/Time Selection -->
          <v-col cols="12" md="6" class="py-2">
            <v-menu
              v-model="date_picker"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="timeStr"
                  label="Date*"
                  outlined
                  dense
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  :error-messages="startDateMessage"
                  @input="startDateMessage = null"
                  class="modern-input"
                  prepend-inner-icon="mdi-calendar"
                />
              </template>
              <v-date-picker
                v-model="startDate"
                scrollable
                :min="minDate"
                color="primary"
                :allowed-dates="allowedDates"
              >
                <v-spacer></v-spacer>
                <v-btn text @click="date_picker = false"> Cancel </v-btn>
                <v-btn color="primary" text @click="onSaveDate"> OK </v-btn>
              </v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" md="6" class="py-2">
            <div class="time-picker-container">
              <vue-timepicker
                format="hh:mm a"
                v-model="startTime"
                @input="timeMessage = null"
                :hour-range="[[6, 22]]"
                hide-clear-button
                advanced-keyboard
                auto-scroll
              ></vue-timepicker>
              <span v-if="timeMessage" class="error-message">
                {{ timeMessage }}
              </span>
            </div>
          </v-col>

          <!-- Shift Hours -->
          <v-col cols="12" md="6" class="py-2">
            <v-text-field
              v-model="hours"
              label="Shift Hours*"
              outlined
              dense
              type="number"
              :min="0.5"
              :step="0.5"
              :error-messages="hourMessage"
              @input="hourMessage = null"
              class="modern-input"
              prepend-inner-icon="mdi-clock-outline"
              suffix="hours"
            />
          </v-col>

          <!-- Shift Capacity -->
          <v-col cols="12" md="6" class="py-2">
            <v-text-field
              v-model="capacity"
              label="Shift Capacity*"
              outlined
              dense
              type="number"
              :min="1"
              :error-messages="capacityMessage"
              @input="capacityMessage = null"
              class="modern-input"
              prepend-inner-icon="mdi-account-group"
              suffix="volunteers"
            >
              <template v-slot:append-outer>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on" small color="grey"
                      >mdi-information</v-icon
                    >
                  </template>
                  Number of volunteer spots available
                </v-tooltip>
              </template>
            </v-text-field>
          </v-col>
        </v-row>

        <div class="text-caption grey--text mt-2">
          *indicates required field
        </div>

        <v-expand-transition>
          <v-alert
            v-if="error"
            dense
            type="error"
            class="mt-4"
            transition="scale-transition"
          >
            {{ error }}
          </v-alert>
        </v-expand-transition>
      </v-card-text>

      <!-- Action buttons -->
      <v-card-actions class="px-6 pb-6 d-flex justify-space-between">
        <v-btn text @click="onClose" color="grey darken-1" class="px-6" large>
          <v-icon left>mdi-close</v-icon>
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          @click="addShift"
          :loading="loading"
          class="px-6 save-btn"
          large
          dark
        >
          <template v-slot:loader>
            <span class="custom-loader">
              <v-icon light>mdi-autorenew</v-icon>
            </span>
          </template>
          <v-icon left>mdi-check</v-icon>
          Create Shift
        </v-btn>
      </v-card-actions>

      <!-- Snackbar for notifications -->
      <v-snackbar v-model="snackbar" :timeout="3000" color="primary" top>
        {{ snackMessage }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar = false"> Close </v-btn>
        </template>
      </v-snackbar>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from "vuex";
//import dateFormat from "dateformat";
import VueTimepicker from "vue2-timepicker";
import "vue2-timepicker/dist/VueTimepicker.css";
import DOMPurify from "dompurify";
export default {
  components: {
    VueTimepicker,
  },
  props: {
    dialog: {
      type: Boolean,
      default: false,
    },
    need_id: String,
    need: Object, // <-- add this line to accept the need object as prop
    onCloseDialog: Function,
    onCreatedShift: Function,
  },
  data() {
    return {
      title: "",
      description: "",
      timeStr: "",
      hours: "",
      capacity: "",
      startDate: null,
      date_picker: false,
      startTime: null,
      timeMessage: null,
      startDateMessage: null,
      titleMessage: null,
      hourMessage: null,
      capacityMessage: null,
      loading: false,
      snackbar: false,
      snackMessage: null,
      error: null,
      minDate: new Date().toISOString().substr(0, 10),
      endDate: null,
      end_date_picker: false,
      endDateStr: "",
      endDateMessage: null,
      rules: {
        required: (value) => !!value || "This field is required",
      },
    };
  },
  computed: {
    allowedDates() {
      // If need and need.duration is not "Is Ongoing", restrict to start_date and end_date range
      if (
        this.need &&
        this.need.duration &&
        this.need.duration !== "Is Ongoing" &&
        this.need.start_date &&
        this.need.end_date
      ) {
        const start = new Date(this.need.start_date);
        const end = new Date(this.need.end_date);
        // Return a function for allowed-dates prop
        return (date) => {
          const d = new Date(date);
          // Only allow dates between start and end (inclusive)
          return d >= start && d <= end;
        };
      }
      // Otherwise, allow all dates from today
      return (date) => {
        const today = new Date();
        const d = new Date(date);
        return d >= today;
      };
    },
  },
  methods: {
    ...mapActions({
      createShift: "need/addShift",
      getTimeZoneName: "usersetting/getTimeZoneName",
    }),
    mapAbbreviationToIANA(tz) {
      const map = {
        EDT: "America/New_York",
        EST: "America/New_York",
        PDT: "America/Los_Angeles",
        PST: "America/Los_Angeles",
        IST: "Asia/Kolkata",
        // Add more as needed
      };
      return map[tz] || tz;
    },
    onClose() {
      // Reset all form fields and error messages, but keep title/description in sync with need
      this.timeStr = "";
      this.hours = "";
      this.capacity = "";
      this.startDate = null;
      this.date_picker = false;
      this.startTime = null;
      this.timeMessage = null;
      this.startDateMessage = null;
      this.titleMessage = null;
      this.hourMessage = null;
      this.capacityMessage = null;
      this.loading = false;
      this.snackbar = false;
      this.snackMessage = null;
      this.error = null;
      this.endDate = null;
      this.end_date_picker = false;
      this.endDateStr = "";
      this.endDateMessage = null;
      // Autofill title/description from need
      if (this.need) {
        this.title = this.need.title || "";
        this.description = this.need.description || "";
      } else {
        this.title = "";
        this.description = "";
      }
      this.onCloseDialog();
    },
    async onSaveDate() {
      if (!this.startDate) return;
      let timezone = await this.getTimeZoneName();
      timezone = this.mapAbbreviationToIANA(timezone);

      // Use the selected date as a string (YYYY-MM-DD)
      // and format it directly in the target timezone
      const dateStr = this.startDate + "T12:00:00"; // Noon avoids DST/offset issues
      const date = new Date(dateStr);

      // Format as MM/DD/YYYY in the target timezone
      const parts = date
        .toLocaleDateString("en-US", {
          timeZone: timezone,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        })
        .split("/");

      // Ensure MM/DD/YYYY format
      this.timeStr = `${parts[0]}/${parts[1]}/${parts[2]}`;

      this.date_picker = false;
      if (this.endDate && new Date(this.endDate) < new Date(this.startDate)) {
        this.endDate = null;
        this.endDateStr = "";
      }
    },
    async onSaveEndDate() {
      if (!this.endDate) return;
      let timezone = await this.getTimeZoneName();
      timezone = this.mapAbbreviationToIANA(timezone);

      const dateStr = this.endDate + "T12:00:00";
      const date = new Date(dateStr);

      const parts = date
        .toLocaleDateString("en-US", {
          timeZone: timezone,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        })
        .split("/");

      this.endDateStr = `${parts[0]}/${parts[1]}/${parts[2]}`;

      this.end_date_picker = false;
    },
    // Utility sanitizer
    sanitizeInput(value) {
      if (!value) return value;

      // Step 1: Sanitize unsafe HTML but allow text inside <script> to survive
      let clean = DOMPurify.sanitize(value, {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
      });

      // Step 2: If DOMPurify returns empty string, fallback to extracting inner text
      if (!clean.trim()) {
        // Create a temporary div to extract text content
        const temp = document.createElement("div");
        temp.innerHTML = value;
        clean = temp.textContent || temp.innerText || "";
      }

      return clean.trim();
    },

    async addShift() {
      // Validate fields
      if (!this.title) {
        this.titleMessage = "Shift title is required";
        return;
      }
      if (!this.timeStr) {
        this.startDateMessage = "Date is required";
        return;
      }
      if (!this.startTime) {
        this.timeMessage = "Start time is required";
        return;
      }
      if (!this.hours) {
        this.hourMessage = "Shift hours are required";
        return;
      }
      if (!this.capacity) {
        this.capacityMessage = "Shift capacity is required";
        return;
      }

      this.loading = true;
      try {
        const timezone = await this.getTimeZoneName();

        // --- Sanitize all inputs ---
        const sanitizedTitle = this.sanitizeInput(this.title);
        const sanitizedDescription = this.sanitizeInput(this.description);
        const sanitizedHours = this.sanitizeInput(this.hours);
        const sanitizedCapacity = this.sanitizeInput(this.capacity);

        // If you have arrays like attributes, sanitize each element
        const sanitizedAttributes = this.need.attributes
          ? this.need.attributes.map((attr) => this.sanitizeInput(attr))
          : [];

        // Build shift object
        const shiftData = {
          title: sanitizedTitle,
          description: sanitizedDescription,
          need_id: this.need_id, // IDs are app-generated, no need to sanitize
          need: this.need, // pass full need object if backend requires
          start_date: `${this.timeStr} ${this.startTime.hh}:${this.startTime.mm} ${this.startTime.a}`,
          // Optional: end_date: this.endDateStr,
          hours: sanitizedHours,
          capacity: sanitizedCapacity,
          attributes: sanitizedAttributes,
          timezone,
        };

        // --- Call Vuex action ---
        const res = await this.createShift(shiftData);

        this.snackMessage = "Shift created successfully!";
        this.snackbar = true;
        this.onCreatedShift(res);
        this.onClose();
      } catch (error) {
        console.error("Error creating shift:", error);
        this.error = error.response?.data?.message || "Failed to create shift";
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.minDate = new Date().toISOString().substr(0, 10);
    // Always autofill shift title and description with need.title and need.description
    if (this.need) {
      this.title = this.need.title || "";
      this.description = this.need.description || "";
    }
  },
  watch: {
    need: {
      handler(newVal) {
        // Always keep title/description in sync with need when dialog is opened
        if (newVal) {
          this.title = newVal.title || "";
          this.description = newVal.description || "";
        }
      },
      deep: true,
      immediate: true,
    },
    dialog(val) {
      // When dialog is opened, always autofill from need
      if (val && this.need) {
        this.title = this.need.title || "";
        this.description = this.need.description || "";
      }
    },
  },
};
</script>

<style scoped>
.modern-shift-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

.editor-header {
  background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
}

.close-btn {
  transition: all 0.3s ease;
}

.close-btn:hover {
  transform: rotate(90deg);
  background: rgba(255, 255, 255, 0.2) !important;
}

.modern-input::v-deep .v-input__slot {
  border-radius: 10px !important;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
  /* border: 2px solid #e0e0e0; */
  background-color: white !important;
}

/* .modern-input::v-deep .v-input__slot:hover {
  border-color: var(--v-primary-base);
} */

.time-picker-container {
  padding-top: 4px;
}

.time-picker-container :deep(.time-picker) {
  width: 100%;
}

.time-picker-container :deep(input.display-time) {
  height: 40px !important;
  width: 100%;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.38);
  padding: 0 15px;
}

.time-picker-container :deep(input.display-time:hover) {
  border-color: rgba(0, 0, 0, 0.87);
}

.time-picker-container :deep(input.display-time:focus) {
  border-color: var(--v-primary-base) !important;
  border-width: 2px;
}

.error-message {
  color: #ff5252;
  font-size: 12px;
  margin-top: 4px;
  display: block;
}

.save-btn {
  box-shadow: 0 4px 12px rgba(58, 123, 213, 0.3) !important;
  transition: all 0.3s ease;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(58, 123, 213, 0.4) !important;
}

.save-btn:active {
  transform: translateY(0);
}

.custom-loader {
  animation: loader 1s infinite;
  display: flex;
}

@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Scrollbar styling */
.modern-shift-dialog::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.modern-shift-dialog::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.modern-shift-dialog::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #3b7ca1 0%, #084468 100%);
  border-radius: 10px;
}

.modern-shift-dialog::-webkit-scrollbar-thumb:hover {
  background: #3a7bd5;
}

@media (max-width: 600px) {
  .time-picker-container :deep(input.display-time) {
    height: 36px !important;
  }

  .v-card__actions {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }
}
</style>
